import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SupportNotificationCenter } from "@/components/SupportNotificationCenter";
import { SupportPushButton } from "@/components/SupportPushButton";
import { SupportTicketComposer } from "@/components/SupportTicketComposer";
import { SupportTicketConversation } from "@/components/SupportTicketConversation";
import { SupportTicketList } from "@/components/SupportTicketList";
import { SUPPORT_STATUSES } from "@/components/SupportStatus";
import { ProductStatus } from "@/components/ProductStatus";
import { useAuth } from "@/lib/auth";
import { useSupportNotifications } from "@/lib/SupportNotificationsContext";
import { useSupportLive } from "@/lib/useSupportLive";
import api from "@/lib/api";

const PAGE_SIZE = 30;

export default function SupportWorkspace() {
  const { user } = useAuth();
  const { ticketId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const notices = useSupportNotifications();
  const admin = user?.role === "superadmin";
  const basePath = admin ? "/admin/assistenza" : "/assistenza";
  const creating = !admin && location.pathname === "/assistenza/nuovo";
  const [bucket, setBucket] = useState("active");
  const [status, setStatus] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [clients, setClients] = useState([]);
  const [page, setPage] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadTabs, setUnreadTabs] = useState({ active: 0, closed: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [liveEvent, setLiveEvent] = useState(null);
  const lastLiveId = useRef(null);
  const requestId = useRef(0);

  useSupportLive(event => {
    setLiveEvent(event);
    notices?.refresh();
  });

  const load = useCallback(async ({ silent = false } = {}) => {
    if (ticketId || creating) return;
    const currentRequest = ++requestId.current;
    if (!silent) setLoading(true);
    setError("");
    try {
      const { data } = await api.get(admin ? "/support/admin/tickets" : "/support/tickets", {
        params: { skip: page * PAGE_SIZE, limit: PAGE_SIZE,
          ...(admin ? { status: status || undefined, tenant_id: tenantId || undefined } : { bucket }),
        },
      });
      if (currentRequest === requestId.current) {
        setTickets(data.items); setTotal(data.total);
      }
    } catch { if (currentRequest === requestId.current) setError("Impossibile caricare i ticket. Riprova."); }
    finally { if (currentRequest === requestId.current) setLoading(false); }
  }, [admin, bucket, status, tenantId, page, ticketId, creating]);

  useEffect(() => {
    const silent = Boolean(liveEvent?.id && liveEvent.id !== lastLiveId.current);
    if (silent) lastLiveId.current = liveEvent.id;
    load({ silent });
  }, [load, liveEvent?.id]);
  useEffect(() => {
    if (!admin) return;
    api.get("/support/admin/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, [admin]);
  useEffect(() => {
    if (admin) return;
    let active = true;
    api.get("/support/tickets/unread-summary").then(({ data }) => {
      if (active) setUnreadTabs(data);
    }).catch(() => {});
    return () => { active = false; };
  }, [admin, ticketId, creating, liveEvent?.id]);

  const chooseBucket = value => { setPage(0); setBucket(value); };
  const chooseStatus = value => { setPage(0); setStatus(value); };
  const chooseClient = value => { setPage(0); setTenantId(value); };

  return (
    <div className="support-ux w-full max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 pt-10 md:pt-14 pb-20 min-w-0" data-testid="support-workspace">
      {!ticketId && !creating && <header className="flex flex-wrap justify-between items-start gap-5 mb-9">
        <div>
          <p className="uppercase text-xs text-[#9A765C] font-semibold mb-2">Cosmely / Assistenza</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A]" data-testid="support-page-title">
            {admin ? "Centro assistenza" : "Assistenza"}
          </h1>
        </div>
        <div className="flex flex-wrap gap-3 items-start">
          <SupportPushButton />
          {!admin && <Button type="button" onClick={() => navigate("/assistenza/nuovo")}
                             className="bg-[#1A1A1A] text-white hover:bg-[#424242] rounded-sm gap-2"
                             data-testid="support-new-button"><Plus className="w-4 h-4" />Nuovo ticket</Button>}
        </div>
      </header>}

      {!ticketId && !creating && <>
        <SupportNotificationCenter />
        <section className="mt-10" aria-label="Ticket di assistenza">
          {admin ? <div className="flex flex-wrap gap-4 mb-8" data-testid="support-admin-filters">
            <label className="text-sm flex flex-col gap-2 min-w-[170px]">Stato
              <select value={status} onChange={event => chooseStatus(event.target.value)}
                      className={`bg-white border border-[#D8D2C6] rounded-sm p-2.5 text-sm ${!status ? "product-ux-select-empty" : ""}`}
                      data-testid="support-filter-status">
                <option value="" data-testid="support-filter-status-all">Tutti gli stati</option>
                {Object.entries(SUPPORT_STATUSES).map(([value, label]) => <option value={value} key={value}
                  data-testid={`support-filter-status-${value.replaceAll("_", "-")}`}>{label}</option>)}
              </select>
            </label>
            <label className="text-sm flex flex-col gap-2 min-w-[190px]">Cliente
              <select value={tenantId} onChange={event => chooseClient(event.target.value)}
                      className={`bg-white border border-[#D8D2C6] rounded-sm p-2.5 text-sm ${!tenantId ? "product-ux-select-empty" : ""}`}
                      data-testid="support-filter-client">
                <option value="" data-testid="support-filter-client-all">Tutti i clienti</option>
                {clients.map(client => <option key={client.id} value={client.id}
                  data-testid={`support-filter-client-${client.id}`}>{client.name}</option>)}
              </select>
            </label>
          </div> : <div role="tablist" aria-label="Storico ticket" className="flex flex-wrap gap-3 sm:gap-8 border-b border-[#E4E3DE] mb-8">
            {[{ value: "active", label: "In corso" }, { value: "closed", label: "Conclusi" }].map(tab => (
              <button key={tab.value} type="button" role="tab" aria-selected={bucket === tab.value}
                      onClick={() => chooseBucket(tab.value)}
                      className={`pb-3 text-sm border-b-2 transition-colors ${bucket === tab.value ? "border-[#1A1A1A] text-[#1A1A1A]" : "border-transparent text-[#707070] hover:text-[#1A1A1A]"}`}
                      data-testid={`support-tab-${tab.value}`}>{tab.label}
                {unreadTabs[tab.value] > 0 && <ProductStatus tone="warning" className="ml-1 sm:ml-2"
                      testId={`support-tab-${tab.value}-unread`}>{unreadTabs[tab.value]}</ProductStatus>}
              </button>
            ))}
          </div>}
          {error && <p role="alert" className="text-sm text-[#8C3A3A] mb-4" data-testid="support-list-error">{error}
            <button type="button" className="underline ml-2" onClick={() => load()} data-testid="support-list-retry">Riprova</button>
          </p>}
          {loading ? <p className="text-sm text-[#707070] py-10" data-testid="support-list-loading">Caricamento ticket...</p>
            : <SupportTicketList tickets={tickets} basePath={basePath} admin={admin} />}
          {total > PAGE_SIZE && <div className="flex items-center gap-4 mt-6 justify-end text-sm" data-testid="support-pagination">
            <Button type="button" variant="outline" size="icon" title="Pagina precedente" aria-label="Pagina precedente"
                    onClick={() => setPage(value => value - 1)} disabled={page === 0}
                    data-testid="support-page-previous"><ChevronLeft className="w-4 h-4" /></Button>
            <span data-testid="support-page-number">{page + 1} / {Math.ceil(total / PAGE_SIZE)}</span>
            <Button type="button" variant="outline" size="icon" title="Pagina successiva" aria-label="Pagina successiva"
                    onClick={() => setPage(value => value + 1)} disabled={(page + 1) * PAGE_SIZE >= total}
                    data-testid="support-page-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>}
        </section>
      </>}
      {creating && <SupportTicketComposer />}
      {ticketId && <SupportTicketConversation ticketId={ticketId} admin={admin} basePath={basePath}
                                               liveEvent={liveEvent} />}
    </div>
  );
}